import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Transition, TransitionStatus } from 'react-transition-group';
import classNames from 'classnames';
import { createPopper, Instance } from '@popperjs/core';
import { useClickOutside } from '@nx-frontend/shared/hooks';
import { BreadcrumbsItem } from '@nx-frontend/shared/types';
// eslint-disable-next-line no-restricted-imports
import { Icon, IconsMap } from '../icon';
// eslint-disable-next-line no-restricted-imports
import { TippyTooltip } from '../tooltip';

const transitionClasses: Record<string, string> = {
  entering: 'opacity-1 transform translate-y-0',
  entered: 'opacity-1 transform translate-y-0',
  exiting: 'opacity-0 transform translate-y-[0px]',
  exited: 'opacity-0 transform translate-y-[-20px]',
};

interface HiddenSegmentsProps {
  breadcrumbs: BreadcrumbsItem[];
  onClick: (item: BreadcrumbsItem) => (e: React.MouseEvent) => void;
}

const HiddenSegments: React.FC<HiddenSegmentsProps> = ({ breadcrumbs, onClick }) => {
  return (
    <div className="p-3 rounded-md drop-shadow-lg bg-white min-w-[180px]">
      {breadcrumbs.map((item, index) => (
        <div
          key={item.id}
          className={classNames('flex items-center text-sm text-neutral-800', { 'mb-1': breadcrumbs.length > 1 })}
          style={{ paddingLeft: index * 8 + 'px' }}
        >
          {index > 0 && <Icon icon={IconsMap.BRANCH_ICON} className="w-3 h-3.5 text-neutral-300 mr-1" />}
          <div
            className={classNames('cursor-pointer hover:text-black duration-200', {
              'mt-3': index > 0,
            })}
            onClick={onClick(item)}
          >
            {item.name}
          </div>
        </div>
      ))}
    </div>
  );
};

interface BreadcrumbsProps {
  items: BreadcrumbsItem[];
  onClick: (e: React.MouseEvent, item: BreadcrumbsItem) => void;
}

const getCrumbShortName = (name: string, _index?: number): string => {
  // let maxLength = 25;
  // if (index > 1) {
  //   maxLength = 20;
  // }
  // return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
  return name;
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items: crumbItems, onClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<BreadcrumbsItem[]>(crumbItems);
  const [hiddenItems, setHiddenItems] = useState<BreadcrumbsItem[]>([]);
  const [showHiddenSegments, setShowHiddenSegments] = useState(false);
  const [hiddenSegmentsVisible, setHiddenSegmentsVisible] = useState(false);
  const [showSegmentTransaction, setShowSegmentTransaction] = useState(false);

  const maxWidth = useRef(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const getItemWidth = useCallback((item: string): number => {
    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'nowrap';
    span.classList.add('text-sm', 'p-0.5', 'inline-block');
    span.textContent = item;
    document.body.appendChild(span);
    const width = span.offsetWidth;
    document.body.removeChild(span);
    return width;
  }, []);

  useEffect(() => {
    if (!wrapperRef?.current) return;
    maxWidth.current = wrapperRef.current.offsetWidth;
  }, [wrapperRef]);

  const hideTransactionTID = useRef<NodeJS.Timeout | null>(null);
  const hideHiddenSegments = useCallback(() => {
    setShowSegmentTransaction(false);
    if (hideTransactionTID.current) {
      clearTimeout(hideTransactionTID.current);
    }
    hideTransactionTID.current = setTimeout(() => {
      setHiddenSegmentsVisible(false);
      setShowHiddenSegments(false);
    }, 600);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setHiddenSegmentsVisible(false);
      setShowHiddenSegments(false);
      setShowSegmentTransaction(false);

      if (!containerRef.current || !maxWidth || crumbItems.length === 0) return;

      if (wrapperRef?.current) {
        const wrapperWidth = wrapperRef.current.offsetWidth;
        if (maxWidth.current !== wrapperWidth) {
          maxWidth.current = wrapperWidth > 0 ? wrapperWidth : 10;
        }
      }

      const items = [...crumbItems].map((item, index) => ({ ...item, shortName: getCrumbShortName(item.name, index) }));

      const startItem = items[0];
      const endItem = items[items.length - 1];
      let newVisibleItems: BreadcrumbsItem[] = [];
      let newHiddenItems: BreadcrumbsItem[] = [];

      let remainingWidth =
        maxWidth.current -
        getItemWidth(startItem.shortName) -
        getItemWidth(endItem.shortName) -
        (items.length - 2 >= 0 ? (items.length - 2) * 29 : 56);

      const firstCrumb: BreadcrumbsItem = {
        ...startItem,
        tooltip: startItem.name,
        dataCy: 'breadcrumbs-firstItem',
        isFirstOne: true,
        truncate: getItemWidth(startItem.shortName) > maxWidth.current,
      };

      if (items.length === 1) {
        setVisibleItems([firstCrumb]);
        setHiddenItems([]);
        return;
      }

      // How much of the last item width should be cut by ...
      const remainPercentage = 0.7;

      /**
       * There are 2 cases
       * 1. When first and last crumbs are visible - remainingWidth > 0
       * 2. When first and last crumbs are not visible together - remainingWidth < 0
       */
      if (remainingWidth > 0) {
        // Limit the maximum number of levels to 4
        if (items.length > 4) {
          newHiddenItems = items.slice(1, items.length - 2); // Все скрытые элементы кроме первой и последних двух

          // Show the first one
          newVisibleItems.push(firstCrumb);

          // Add ... as the second item
          newVisibleItems.push({
            name: '...',
            url: '',
            id: '...',
            tooltip: newHiddenItems.map((item) => item.name).join(' > '),
          });

          // Check if the second last item fits
          const secondLastItem = items[items.length - 2];
          if (remainingWidth - getItemWidth(secondLastItem.shortName) > 0) {
            newVisibleItems.push({
              ...secondLastItem,
              tooltip: secondLastItem.name,
              dataCy: 'breadcrumbs-second-last',
            });
          }

          // Add the last crumb
          newVisibleItems.push({ ...endItem, tooltip: endItem.name, dataCy: 'breadcrumbs-lastItem', isLastOne: true });
        } else {
          // Add remainPercentage of the last item width to the remaining width, to make more space for other items
          // last one remainPercentage of last one will cut by ...
          remainingWidth += getItemWidth(endItem.shortName) * remainPercentage;
          // set first crumb
          newVisibleItems.push(firstCrumb);

          const visibleItems: BreadcrumbsItem[] = [];

          let isInvisibleSet = false;
          // start from prev last item till 2nd one
          for (let i = items.length - 2; i >= 1; i--) {
            const itemWidth = getItemWidth(items[i].shortName);
            // check if there is enough space for the item and isInvisibleSet still false
            if (remainingWidth - itemWidth > 0 && !isInvisibleSet) {
              visibleItems.push({ ...items[i], tooltip: items[i].name, dataCy: 'breadcrumbs-item' });
              remainingWidth -= itemWidth;
            } else {
              // if not enough space set isInvisibleSet to true and add item to hidden items
              isInvisibleSet = true;
              newHiddenItems.push(items[i]);
            }
          }
          // if there are hidden items add ... item
          if (newHiddenItems.length > 0) {
            newVisibleItems.splice(1, 0, {
              name: '...',
              url: '',
              id: '...',
              tooltip: newHiddenItems.map((item) => item.name).join(' > '),
            });
          }
          // add last item
          newVisibleItems.push({ ...endItem, tooltip: endItem.name, dataCy: 'breadcrumbs-lastItem', isLastOne: true });
          // reverse visible items and add last item again
          newVisibleItems = [
            ...newVisibleItems.slice(0, newHiddenItems.length ? 2 : 1),
            ...visibleItems.reverse(),
            newVisibleItems[newVisibleItems.length - 1],
          ];
          // reverse hidden items
          newHiddenItems = newHiddenItems.reverse();
        }
      } else {
        // Second case when first and last crumbs are not visible together
        remainingWidth =
          maxWidth.current -
          getItemWidth(endItem.shortName) -
          (items.length - 1) * 24 +
          getItemWidth(endItem.shortName) * remainPercentage;
        // add .. as first item
        newVisibleItems.push({
          name: '...',
          url: '',
          id: '...',
        });

        // Collect visible and hidden items
        let isInvisibleSet = false;
        for (let i = items.length - 2; i >= 0; i--) {
          const itemWidth = getItemWidth(items[i].shortName);
          if (remainingWidth - itemWidth > 0 && !isInvisibleSet) {
            newVisibleItems.push({ ...items[i], tooltip: items[i].name, dataCy: 'breadcrumbs-item' });
            remainingWidth -= itemWidth;
          } else {
            isInvisibleSet = true;
            newHiddenItems.push(items[i]);
          }
        }

        // if there are hidden items add last item
        newVisibleItems = [
          ...(newHiddenItems.length ? [newVisibleItems[0]] : []),
          ...newVisibleItems.splice(1).reverse(),
        ];
        // add last item
        newVisibleItems.push({ ...endItem, tooltip: endItem.name, dataCy: 'breadcrumbs-lastItem', isLastOne: true });
        newHiddenItems = newHiddenItems.reverse();
      }

      setVisibleItems(newVisibleItems);
      setHiddenItems(newHiddenItems);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('sidebar:resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('sidebar:resize', handleResize);
    };
  }, [getItemWidth, hideHiddenSegments, crumbItems, wrapperRef]);

  const moreRef = useRef<HTMLSpanElement>(null);
  const hiddenSegmentsRef = useRef<HTMLDivElement>(null);
  const popperInstanceRef = useRef<Instance | null>(null);

  const destroyPopper = useCallback(() => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.destroy();
      popperInstanceRef.current = null;
    }
  }, []);

  const hideTIDRef = useRef<NodeJS.Timeout | null>(null);

  const handleMoreMouseEnter = useCallback(() => {
    if (hideTIDRef.current) {
      clearTimeout(hideTIDRef.current);
    }
    if (!showHiddenSegments) {
      destroyPopper();
      setShowHiddenSegments(true);
      setTimeout(() => {
        if (moreRef.current && hiddenSegmentsRef.current) {
          popperInstanceRef.current = createPopper(moreRef.current, hiddenSegmentsRef.current, {
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 10],
                },
              },
            ],
          });
          setHiddenSegmentsVisible(true);
          setShowSegmentTransaction(true);
        }
      }, 5);
    } else if (!showSegmentTransaction) {
      if (hideTransactionTID.current) {
        clearTimeout(hideTransactionTID.current);
      }
      setHiddenSegmentsVisible(true);
      setShowSegmentTransaction(true);
    }
  }, [destroyPopper, showHiddenSegments, showSegmentTransaction]);

  const handleMoreMouseLeave = useCallback(() => {
    hideTIDRef.current = setTimeout(() => {
      if (showHiddenSegments) {
        hideHiddenSegments();
      }
    }, 1000);
  }, [hideHiddenSegments, showHiddenSegments]);

  const handleMoreClick = useCallback(() => {
    if (showHiddenSegments) {
      hideHiddenSegments();
    } else {
      handleMoreMouseEnter();
    }
  }, [handleMoreMouseEnter, hideHiddenSegments, showHiddenSegments]);

  const handleCrumbClick = useCallback(
    (item: BreadcrumbsItem) => (e: React.MouseEvent) => {
      if (item.url) {
        onClick(e, item);
        hideHiddenSegments();
      }
    },
    [hideHiddenSegments, onClick]
  );

  useClickOutside({
    enabled: showHiddenSegments,
    elementRef: hiddenSegmentsRef,
    skipSelectors: ['.breadcrumbs__more-btn'],
    onClickOutside: () => hideHiddenSegments(),
  });

  useEffect(() => {
    return () => {
      destroyPopper();
    };
  }, [destroyPopper]);

  return (
    <div ref={wrapperRef} className="w-full text-sm min-w-[100px]">
      {maxWidth ? (
        <div
          ref={containerRef}
          className={classNames('flex items-center overflow-hidden whitespace-nowrap')}
          style={{ maxWidth: maxWidth.current }}
        >
          {visibleItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.id !== '...' ? (
                <TippyTooltip message={item.tooltip} position="bottom">
                  <span
                    data-cy={item.dataCy}
                    onClick={handleCrumbClick(item)}
                    className={classNames('p-0.5', {
                      'text-ellipsis overflow-hidden': item.isLastOne,
                      'inline-block': item.isFirstOne,
                      'cursor-pointer': !!item.url,
                    })}
                  >
                    {item.shortName}
                  </span>
                </TippyTooltip>
              ) : (
                <span
                  ref={moreRef}
                  data-cy={item.dataCy}
                  onMouseEnter={handleMoreMouseEnter}
                  onMouseLeave={handleMoreMouseLeave}
                  onClick={handleMoreClick}
                  className={classNames(
                    'breadcrumbs__more-btn cursor-pointer hover:bg-neutral-100 inline-block w-5 h-6 text-center rounded duration-200 shrink-0',
                    {
                      'bg-neutral-100': showSegmentTransaction,
                    }
                  )}
                >
                  ...
                </span>
              )}

              {index < visibleItems.length - 1 && (
                <span className="mx-0.5 shrink-0">
                  <Icon icon={IconsMap.ARROW_RIGHT_3} className="size-[12px] text-neutral-800" />
                </span>
              )}
            </React.Fragment>
          ))}
          {showHiddenSegments
            ? createPortal(
                <div
                  className={classNames('absolute top-0 left-0 z-[100]', {
                    'invisible': !hiddenSegmentsVisible,
                    'pointer-events-none': !showSegmentTransaction,
                  })}
                  ref={hiddenSegmentsRef}
                  onMouseEnter={handleMoreMouseEnter}
                  onMouseLeave={handleMoreMouseLeave}
                >
                  <Transition
                    in={showSegmentTransaction}
                    nodeRef={hiddenSegmentsRef}
                    timeout={600}
                    unmountOnExit={true}
                  >
                    {(state: TransitionStatus) => (
                      <div
                        data-state={state}
                        ref={hiddenSegmentsRef}
                        className={`top-0 transition-all ${transitionClasses[state]}`}
                      >
                        <HiddenSegments breadcrumbs={hiddenItems} onClick={handleCrumbClick} />
                      </div>
                    )}
                  </Transition>
                </div>,
                document.body
              )
            : null}
        </div>
      ) : null}
    </div>
  );
};
